import React from 'react';
import classNames from 'classnames';
import { RadioStyled, RadioSetStyled } from '@nike/nike-design-system-components';
import { connect } from 'react-redux';

import { OptionGrid, Button } from '../../components';
import Filter from '~COMPONENTS/filters/Filter';
import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { filtersTypes } from '../../types/shop';

import FilterCheckbox from './FilterCheckbox';

import { FILTER_SIZE, LABEL_SELECT_ALL, ORDER_TYPE_ATONCE } from '../constants';
import { selectAllSizesAvailable } from '../selectors';

function normalizeValue(value) {
  return value.replace('atonce', 'at-once');
}

function renderSizes(
  name,
  values,
  selected,
  onChange,
  handleSizeAvailabilityChange,
  allSizesAvailable,
  selectedDepartmentType
) {
  // to group all unisex sizes at end of array for slice in OptionGrid
  const reg = new RegExp(/M.*\/.*W/i);

  const normalizedValues = values
    .sort((a, b) => {
      if (reg.test(a)) return 1;
      if (reg.test(b)) return -1;
      return a - b;
    })
    .map(val => ({
      name: normalizeValue(val),
      value: `${name}--${normalizeValue(val)}`
    }));
  const selectedSizes = selected.size || [];

  return (
    <>
      {selectedDepartmentType === ORDER_TYPE_ATONCE && (
        <div
          style={{
            display: normalizedValues.length <= 0 ? 'none' : 'block'
          }}
        >
          <div
            style={{
              lineHeight: '1.25',
              fontSize: '12px',
              fontWeight: '500',
              paddingTop: '5px'
            }}
          >
            <TranslatedCopy id='gridwall.filters.size.availabilitySubtext' />
          </div>
          <RadioSetStyled
            legend='Size Availability'
            name='sizeAvailability'
            onChange={handleSizeAvailabilityChange}
            selectedValue={allSizesAvailable ? 'all' : 'atLeastOne'}
            classes={{ fieldSet: 'sizesAvailableFilterContainer' }}
            style={{
              marginBottom: '15px',
              marginLeft: '-10px'
            }}
          >
            <RadioStyled
              id='atLeastOneSizeAvailable'
              label={new TranslatedCopy({ id: 'gridwall.filters.size.someSizesMustHaveAvailability' }).toString()}
              value='atLeastOne'
              classes={{ label: 'allSizesAvailableFilterLabel' }}
            />
            <RadioStyled
              id='allSizesAvailable'
              label={new TranslatedCopy({ id: 'gridwall.filters.size.allSizesMustHaveAvailability' }).toString()}
              value='all'
              classes={{ label: 'allSizesAvailableFilterLabel' }}
            />
          </RadioSetStyled>
        </div>
      )}
      <OptionGrid
        options={normalizedValues}
        selectedSizes={selectedSizes}
        onChange={onChange}
        style={{
          marginTop: selectedDepartmentType !== ORDER_TYPE_ATONCE && '23px' // mimic filter button padding
        }}
      />
    </>
  );
}

export function renderCheckboxes(name, values, selected, onChange, selectAll, pageName, isTranslated) {
  const isColor = name === 'color';
  const isDeparments = name === 'departmentName' || name === 'departmentNames';

  return (
    <ul className={classNames({ 'colorSelection flex-container wrap start row': isColor })}>
      {isDeparments && (
        <li>
          <FilterCheckbox
            label={LABEL_SELECT_ALL}
            name={LABEL_SELECT_ALL}
            onChange={onChange}
            checked={selectAll}
            isColor={false}
          />
        </li>
      )}

      {values.map(item => {
        const currItem = item.value ? item.value : normalizeValue(item);
        const translatedLabelVal = `${pageName}.${name}.${currItem.toLowerCase().split(' ').join('')}`;
        // eslint-disable-next-line no-nested-ternary
        const label = item.label ? item.label : isTranslated && !isDeparments ? translatedLabelVal : currItem;

        return (
          <li key={currItem} title={isColor ? `${currItem}` : null}>
            <FilterCheckbox
              label={label}
              name={`${name}--${currItem}`}
              onChange={onChange}
              // selected is either a JS set or Immutable set
              checked={selected?.includes?.(currItem) || selected?.has?.(currItem)}
              isColor={isColor}
            />
          </li>
        );
      })}
    </ul>
  );
}

export const Filters = ({
  filters,
  selected, // selected is either a JS map or Immutable map
  count,
  clear,
  onChange,
  pageName,
  showFilterCount,
  selectAll,
  closeOnOutsideClick,
  isTranslated,
  handleSizeAvailabilityChange,
  allSizesAvailable,
  selectedDepartmentType
}) => (
  <div className='filters border-top'>
    {clear && (
      <div className='p5-sm headline-5 u-bold'>
        <TranslatedCopy id='global.filter' />
        {count > 0 && (
          <Button
            className='text-color-secondary body-4 p0-sm text-underline fl-sm-r	'
            data-desc='gridwall__filter_clear'
            useBareClass
            textLink
            onClick={clear}
          >
            <TranslatedCopy id='gridwall.filters.clear' />
          </Button>
        )}
      </div>
    )}
    {filters.map(({ key, values, subText }) => {
      const normalizedName = key.toLowerCase().includes('departmentname') ? 'departments' : key.toLowerCase();
      const translatedNameVal =
        normalizedName === 'color' || normalizedName === 'departments'
          ? `global.${normalizedName}`
          : `${pageName}.filters.${normalizedName}`;
      // TODO: Refactor in the future with B-384780
      const selectedCount = selected.values || 0;
      const trigger =
        showFilterCount && selectedCount > 0
          ? `${new TranslatedCopy({ id: translatedNameVal }).toString()} (${
              selected?.[key]?.length || selected?.get?.(key)?.size
            })`
          : translatedNameVal;

      return values.length || key === FILTER_SIZE ? (
        <Filter key={key} trigger={trigger} closeOnOutsideClick={closeOnOutsideClick} subText={subText}>
          {key === FILTER_SIZE
            ? renderSizes(
                key,
                values,
                selected,
                onChange,
                handleSizeAvailabilityChange,
                allSizesAvailable,
                selectedDepartmentType
              )
            : renderCheckboxes(
                key,
                values,
                selected?.get?.(key) || selected?.[key],
                onChange,
                selectAll,
                pageName,
                isTranslated
              )}
        </Filter>
      ) : null;
    })}
  </div>
);

Filters.defaultProps = {
  closeOnOutsideClick: false,
  filters: [],
  count: null,
  productCount: null,
  selectAll: false,
  allSizesAvailable: false
};

Filters.propTypes = {
  ...filtersTypes
};

const mapStateToProps = state => {
  return {
    allSizesAvailable: selectAllSizesAvailable(state)
  };
};

export default connect(mapStateToProps)(Filters);
